// Shared shell for the right-rail info panel — header + dismiss button +
// scroll body. Specific panels (TileInfoPanel, CityInfoPanel, future hero
// panel) compose into this.

import { Button } from '@aod/ui/components/ui/button';
import { ScrollArea } from '@aod/ui/components/ui/scroll-area';
import { Info, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '../../i18n/index.js';

interface InfoPanelShellProps {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly onClose: () => void;
  readonly onInfo?: () => void;
  /** Art for the head of the panel (a `SceneImage`); sits above the title. */
  readonly media?: ReactNode;
  readonly children: ReactNode;
}

export function InfoPanelShell({
  title,
  subtitle,
  onClose,
  onInfo,
  media,
  children,
}: InfoPanelShellProps) {
  const t = useT();
  return (
    <aside
      aria-label={title}
      className="fixed top-(--hud-panel-top) right-3 bottom-(--right-rail-bottom) z-40 flex w-[280px] flex-col gap-2 rounded-xl border border-border/70 p-3 text-foreground shadow-xl backdrop-blur-sm animate-fade-in info-panel-compact"
      style={{
        background: `linear-gradient(var(--panel-scrim), var(--panel-scrim)), url('/assets/images/ui/scroll_bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {media}
      <header className="flex items-start justify-between gap-2 px-1 pt-1 pb-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
            {t('rightPanel.info')}
          </span>
          <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
          {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        <div className="flex shrink-0 gap-0.5 -mt-0.5">
          {onInfo && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('rightPanel.viewDetails')}
              onClick={onInfo}
              className="size-7"
            >
              <Info className="size-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('rightPanel.closeInfo')}
            onClick={onClose}
            className="size-7"
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>
      <ScrollArea className="-mx-1 flex-1 px-1">
        <div className="flex flex-col gap-3 py-1">{children}</div>
      </ScrollArea>
    </aside>
  );
}

interface RowProps {
  readonly label: ReactNode;
  readonly children: ReactNode;
}

export function Row({ label, children }: RowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums text-foreground">{children}</span>
    </div>
  );
}
